import React, { useState } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Collapse,
  Button,
  Table,
  Alert,
} from 'reactstrap';
import { Form } from '@availity/form';
import Select from '@availity/select';
import '@availity/yup';
import * as yup from 'yup';

import * as usersActions from '../../store/users/actions';
import { selectActiveAcquisitionOrdersForSelect } from '../../selectors/acquisitionOrder';
import { useSelector, useDispatch } from 'react-redux';

import { useTranslation } from 'react-i18next';
import { Dots } from 'react-activity';
import { selectTranslation } from '../../helpers/sharedFunction';

import {
  MAX_SELECTED_ACQUISITION_ORDERS,
  batchEditModes,
} from '../../config/acquisitionOrders';

const EditAcquisitionOrders = ({ users }) => {
  const [acquisitionOrderIds, setAcquisitionOrderIds] = useState([]);
  const [mode, setMode] = useState(batchEditModes.ADD);
  const [sent, setSent] = useState(false);

  const countryId = useSelector((state) => state.Dashboard.countryId);
  const acquisitionOrdersForSelect = useSelector(
    selectActiveAcquisitionOrdersForSelect,
  );

  const loading = useSelector((state) => state.User.loading);
  const logs = useSelector((state) => state.User.logs);

  const dispatch = useDispatch();

  const { t } = useTranslation();

  const currentAcquisitionOrderIds = (user) =>
    user.acquisitionOrderIds?.[countryId] || [];

  const updatedAcquisitionOrderIds = (user) =>
    mode === batchEditModes.ADD
      ? [
          ...new Set([
            ...currentAcquisitionOrderIds(user),
            ...acquisitionOrderIds,
          ]),
        ]
      : [...new Set(acquisitionOrderIds)];

  const exceedingUsers = users.filter(
    (user) =>
      updatedAcquisitionOrderIds(user).length > MAX_SELECTED_ACQUISITION_ORDERS,
  );
  const editableUsers = users.filter((user) => !exceedingUsers.includes(user));

  const handleEditAcquisitionOrders = () => {
    dispatch(
      usersActions.updateUsersAcquisitionOrders(
        editableUsers,
        acquisitionOrderIds,
        mode,
      ),
    );
    setSent(true);
  };

  const editModeOptions = selectTranslation(
    [
      {
        value: batchEditModes.ADD,
        label: 'Add to current Acquisition Orders',
      },
      {
        value: batchEditModes.REPLACE,
        label: 'Replace current Acquisition Orders',
      },
    ],
    t,
  );

  return (
    <Card>
      <CardBody>
        <Form
          initialValues={{ mode, acquisitionOrderIds }}
          validationSchema={yup.object().shape({
            mode: yup.string().required(),
            acquisitionOrderIds: yup.array().of(yup.string()).min(1),
          })}>
          <Row>
            <Col md="4">
              <FormGroup>
                <Label htmlFor="mode">
                  {t('edit_acquisition_orders_mode_label')}
                </Label>
                <Select
                  name="mode"
                  id="mode"
                  isMulti={false}
                  onChange={(value) => setMode(value)}
                  value={mode}
                  isDisabled={sent}
                  options={editModeOptions}
                />
              </FormGroup>
            </Col>
            <Col md="8">
              <FormGroup>
                <Label htmlFor="acquisitionOrderIds">
                  {t('Acquisition Orders')}:
                </Label>
                <Select
                  name="acquisitionOrderIds"
                  id="acquisitionOrderIds"
                  isMulti
                  maxLength={MAX_SELECTED_ACQUISITION_ORDERS}
                  isSearchable
                  onChange={(values) => setAcquisitionOrderIds(values || [])}
                  value={acquisitionOrderIds}
                  isDisabled={sent}
                  options={acquisitionOrdersForSelect}
                />
                <p className="font-size-12 text-muted mb-0">
                  {t('only_active_acquisition_orders')}
                </p>
              </FormGroup>
            </Col>
          </Row>
          <Collapse isOpen={!!acquisitionOrderIds.length && !sent}>
            <Row className="my-2">
              <Col md="12">
                <h4 className="text-center">
                  {t('edit_acquisition_orders', {
                    countUsers: editableUsers.length,
                  })}
                </h4>
              </Col>
            </Row>
            {!!exceedingUsers.length && (
              <Row className="my-2">
                <Col md="12">
                  <Alert color="warning" className="text-center mb-0">
                    <i
                      className={`bx bx-error-circle font-size-20 align-middle mr-2`}></i>
                    {t('edit_acquisition_orders_limit_warning', {
                      countUsers: exceedingUsers.length,
                      maxAcquisitionOrders: MAX_SELECTED_ACQUISITION_ORDERS,
                    })}
                  </Alert>
                </Col>
              </Row>
            )}
            <Row className="my-2">
              <Col md="12">
                <div className="table-responsive" style={{ maxHeight: '300px' }}>
                  <Table className="table mb-0">
                    <thead>
                      <tr>
                        <th>{t('Display Name')}</th>
                        <th>{t('Role')}</th>
                        <th className="text-center">
                          {t('Current Acquisition Orders')}
                        </th>
                        <th className="text-center">
                          {t('New Acquisition Orders')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => {
                        const skipped = exceedingUsers.includes(user);
                        return (
                          <tr key={user.id}>
                            <td>{user.displayName || user.email}</td>
                            <td>{user.role}</td>
                            <td className="text-center">
                              {currentAcquisitionOrderIds(user).length}
                            </td>
                            <td className="text-center">
                              <span
                                className={`badge badge-${
                                  skipped ? 'danger' : 'success'
                                } font-size-12`}>
                                {updatedAcquisitionOrderIds(user).length}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </Col>
            </Row>
            <Row className="my-2">
              <Col md="12" className="text-center">
                <Button
                  color="info"
                  outline
                  className="waves-effect waves-light"
                  onClick={handleEditAcquisitionOrders}
                  disabled={
                    !editableUsers.length || !acquisitionOrderIds.length || sent
                  }>
                  <i
                    className={`bx bx-wallet font-size-20 align-middle mr-2`}></i>
                  {t('Edit Acquisition Orders')}
                </Button>
              </Col>
            </Row>
          </Collapse>
        </Form>
        {!!(sent && loading) && <Dots />}
        {!!(sent && logs.length) && (
          <Row className="my-2">
            <Col md="12">
              <div className="table-responsive">
                <Table className="table mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t('Display Name')}</th>
                      <th>Response</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log, index) => (
                      <tr key={log.userId}>
                        <th scope="row">{index}</th>
                        <td>{log.displayName}</td>
                        <td
                          className={
                            log.status === 200 ? 'text-success' : 'text-danger'
                          }>
                          {log.response}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Col>
          </Row>
        )}
      </CardBody>
    </Card>
  );
};

export default EditAcquisitionOrders;
